import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { api, useAuth } from '@/contexts/AuthContext';
import GoogleSheetsPicker from './GoogleSheetsPicker';
import { connectGoogleSheet, getAvailableSheets, getConnectedSheets, getGoogleSheetsAuthStatus, getGoogleSheetsAuthorizationUrl, getGoogleSheetTabs, refreshAvailableGoogleSheets, SheetTab } from "@/lib/googleSheetsApi";
import { Search, Sheet, Plus, ExternalLink, RotateCcw, CheckCircle2, ArrowLeft } from 'lucide-react';
import { ConnectedSheet, AvailableSheet, SheetsPagination, SheetDataLoadResponse } from '@/types';

interface InlineSheetsSelectorProps {
  onSheetSelected: (
    sheetId: string,
    sheetData?: SheetDataLoadResponse,
    tabName?: string,
    tabId?: number | null
  ) => void;
  onBack?: () => void;
  onNext?: () => void;
  selectedSheetId?: string;
  campaignType?: string;
  excludedTabNames?: string[];
}

const InlineSheetsSelector: React.FC<InlineSheetsSelectorProps> = ({
  onSheetSelected, onBack, onNext, selectedSheetId, campaignType = 'Marketing', excludedTabNames = []
}) => {
  const { user, token } = useAuth();
  const [sheets, setSheets] = useState<ConnectedSheet[]>([]);
  const [availableSheets, setAvailableSheets] = useState<AvailableSheet[]>([]);
  const [pagination, setPagination] = useState<SheetsPagination>({ page: 1, limit: 9, total: 0, total_pages: 0, has_next: false, has_prev: false });
  const [loading, setLoading] = useState<boolean>(true);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showAvailableSheets, setShowAvailableSheets] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loadingSheetData, setLoadingSheetData] = useState<boolean>(false);
  const [sheetDataLoaded, setSheetDataLoaded] = useState<boolean>(false);
  const [availableTabs, setAvailableTabs] = useState<SheetTab[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [loadingTabs, setLoadingTabs] = useState<boolean>(false);
  const [pendingSheetId, setPendingSheetId] = useState<string | null>(null);

  useEffect(() => {
    if (user && token) { initializeComponent(); }
  }, [user, token]);

  // Resetear estado cuando cambia la hoja seleccionada desde el padre
  useEffect(() => {
    if (selectedSheetId) {
      setSheetDataLoaded(true);
    }
  }, [selectedSheetId]);

  const initializeComponent = async (): Promise<void> => {
    try {
      setLoading(true);
      const isAuth = await checkAuthStatus();
      await loadConnectedSheets();
      if (isAuth && sheets.length === 0) { await loadAvailableSheets(1); }
    } catch (error) {
      console.error('Error initializing component:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      const authStatus = await getGoogleSheetsAuthStatus();
      const authenticated = authStatus.is_authenticated && !authStatus.needs_auth;
      setIsAuthenticated(authenticated);
      return authenticated;
    } catch (error) {
      setIsAuthenticated(false);
      return false;
    }
  };

  const loadConnectedSheets = async (): Promise<void> => {
    if (!user || !token) return;
    try {
      const connectedSheets = await getConnectedSheets();
      setSheets(connectedSheets || []);
    } catch (error) {
      toast.error('Error al cargar las hojas conectadas');
      setSheets([]);
    }
  };

  const loadAvailableSheets = async (page: number = 1): Promise<void> => {
    if (!user || !token) return;
    try {
      setLoading(true);
      const data = await getAvailableSheets(page, pagination.limit);
      setAvailableSheets(data.sheets || []);
      setPagination(data.pagination || { page: 1, limit: 9, total: 0, total_pages: 0, has_next: false, has_prev: false });
      setShowAvailableSheets(true);
    } catch (error) {
      toast.error('Error al cargar hojas disponibles');
    } finally {
      setLoading(false);
    }
  };

  const connectSheet = async (sheetId: string): Promise<void> => {
    if (!token) return;
    setConnectingId(sheetId);
    try {
      await connectGoogleSheet(sheetId);
      toast.success('Hoja conectada exitosamente');
      await loadConnectedSheets();
      handleSheetSelection(sheetId);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Error al conectar la hoja');
    } finally {
      setConnectingId(null);
    }
  };

const handleSheetSelection = async (sheetId: string): Promise<void> => {
  try {
    setLoadingTabs(true);
    setSheetDataLoaded(false);
    setSelectedTab('');
    setAvailableTabs([]);
    setPendingSheetId(sheetId);

    const tabs = await getGoogleSheetTabs(sheetId);
    const visibleTabs = tabs.filter((tab) => !tab.hidden);

    if (visibleTabs.length === 0) {
      setPendingSheetId(null);
      toast.error('La hoja seleccionada no tiene pestañas disponibles');
      return;
    }

    setAvailableTabs(visibleTabs);
    toast.success('Selecciona la pestaña que deseas utilizar');
  } catch (error: any) {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      toast.error('Tu sesión de Google expiró. Por favor reconecta tu cuenta.');
      setIsAuthenticated(false);
      return;
    }

    setPendingSheetId(null);
    toast.error('Error al cargar las pestañas de la hoja');
  } finally {
    setLoadingTabs(false);
  }
};

const handleTabSelection = async (tabName: string): Promise<void> => {
  if (!pendingSheetId || !tabName) return;

  const selectedTabInfo = availableTabs.find(
    (tab) => tab.title === tabName
  );

  const tabId = selectedTabInfo?.sheet_id ?? null;

  try {
    setLoadingSheetData(true);
    setSheetDataLoaded(false);
    setSelectedTab(tabName);

    const response = await api.get<SheetDataLoadResponse>(
      `/google-sheets/${pendingSheetId}/data`,
      {
        params: {
          max_rows: 5,
          sheet_name: tabName,
          ...(tabId != null
            ? { google_sheet_tab_id: tabId }
            : {}),
        },
      }
    );

    if (response.data && response.data.columns && response.data.columns.length > 0) {
      onSheetSelected(
        pendingSheetId,
        response.data,
        tabName,
        tabId
      );
      setSheetDataLoaded(true);
      toast.success(`Pestaña "${tabName}" seleccionada correctamente`);
    } else {
      setSheetDataLoaded(false);
      toast.error('La pestaña seleccionada no tiene datos válidos');
    }
  } catch (error: any) {
    setSheetDataLoaded(false);

    if (error?.response?.status === 401 || error?.response?.status === 403) {
      toast.error('Tu sesión de Google expiró. Por favor reconecta tu cuenta.');
      setIsAuthenticated(false);
      return;
    }

    toast.error('Error al cargar los datos de la pestaña');
  } finally {
    setLoadingSheetData(false);
  }
};

  // ✅ CORREGIDO: Replicar lógica exacta de GoogleSheetsIntegration.handleGoogleAuth
  const handleConnectOrSelectSheets = async (): Promise<void> => {
    console.log('🚀 handleConnectOrSelectSheets called');
    setAuthLoading(true);

    try {
      // 1. Verificar si ya está autenticado (igual que GoogleSheetsIntegration)
      console.log('🔍 Checking existing Google authentication...');
      const authStatus = await getGoogleSheetsAuthStatus({ force: true });
      console.log('📊 Auth status response:', authStatus);

      if (authStatus.is_authenticated && !authStatus.needs_auth) {
        // ✅ Usuario YA autenticado → refrescar hojas desde Google Drive
        console.log('✅ User already authenticated, refreshing sheets from Google Drive...');
        toast.info('Actualizando hojas desde Google Drive...', { duration: 2000 });

        try {
          console.log('📡 Calling /google-sheets/load-available-direct...');
          // Llamar al backend para refrescar hojas desde Google Drive API
          await refreshAvailableGoogleSheets();
          console.log('✅ Refreshed sheets from Google Drive');
          toast.success('Hojas actualizadas desde Google Drive');
        } catch (refreshError: any) {
          console.error('❌ Error refreshing sheets:', refreshError);
          if (refreshError.response?.status === 401) {
            toast.error('Tu sesión de Google expiró. Por favor reconecta tu cuenta.');
            // Caer al flujo OAuth
            const authorizationUrl = await getGoogleSheetsAuthorizationUrl('campaign-builder');
            if (!authorizationUrl) throw new Error('No authorization URL received');
            window.location.href = authorizationUrl;
            return;
          }
          // Continuar aunque falle el refresh
          toast.warning('Error al actualizar hojas, mostrando las disponibles en caché');
        }

        // Cargar hojas disponibles y mostrarlas
        await loadAvailableSheets(1);
        setIsAuthenticated(true);
        toast.success('Hojas cargadas. Selecciona una para continuar.');

      } else {
        // ❌ Usuario NO autenticado → iniciar OAuth
        console.log('🔐 User needs authentication, starting OAuth flow...');
        const authorizationUrl = await getGoogleSheetsAuthorizationUrl('campaign-builder');
        if (!authorizationUrl) throw new Error('No authorization URL received');
        window.location.href = authorizationUrl;
      }

    } catch (error: any) {
      console.error('Error checking auth status:', error);
      toast.error('Error verificando autenticación. Iniciando proceso completo...');
      // Caer al flujo OAuth en caso de error
      try {
        const authorizationUrl = await getGoogleSheetsAuthorizationUrl('campaign-builder');
        if (!authorizationUrl) throw new Error('No authorization URL received');
        window.location.href = authorizationUrl;
      } catch (oauthError) {
        toast.error('Error al iniciar la autenticación con Google');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const filteredSheets = sheets.filter(sheet => sheet.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredAvailableSheets = availableSheets.filter(sheet => sheet.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2"><Sheet className="h-5 w-5" /><span>Seleccionar Hoja de Contactos</span></CardTitle>
          <CardDescription>Cargando hojas disponibles...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2"><Sheet className="h-5 w-5 text-blue-600" /><span>Seleccionar Hoja de Contactos</span></CardTitle>
            <CardDescription>Elige la hoja de Google Sheets con los contactos para tu campaña de {campaignType}</CardDescription>
          </div>
          {onBack && (<Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Atrás</Button>)}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input type="text" placeholder="Buscar hoja por nombre..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>

        {pendingSheetId && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona la pestaña de Google Sheets
            </label>

            {loadingTabs ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                Cargando pestañas...
              </div>
            ) : (
              <select
                value={selectedTab}
                onChange={(e) => handleTabSelection(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">Seleccionar pestaña...</option>

                {availableTabs.map((tab) => (
                  <option key={tab.sheet_id} value={tab.title}>
                    {tab.title}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {filteredSheets.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Hojas Conectadas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSheets.map((sheet) => (
                <Card key={sheet.sheet_id} className={`cursor-pointer transition-all hover:shadow-md ${selectedSheetId === sheet.sheet_id ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`} onClick={() => handleSheetSelection(sheet.sheet_id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><Sheet className="h-5 w-5 text-green-600" /></div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 truncate" title={sheet.name}>{sheet.name}</h4>
                          <p className="text-sm text-gray-500">{new Date(sheet.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {selectedSheetId === sheet.sheet_id && (<CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />)}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Conectada</Badge>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); window.open(sheet.url, '_blank'); }}><ExternalLink className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {isAuthenticated && showAvailableSheets && filteredAvailableSheets.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Hojas Disponibles en Google Drive</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAvailableSheets.map((sheet) => (
                <Card key={sheet.sheet_id} className="hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"><Sheet className="h-5 w-5 text-blue-600" /></div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-gray-900 truncate" title={sheet.name}>{sheet.name}</h4>
                          <p className="text-sm text-gray-500">{sheet.last_modified ? new Date(sheet.last_modified).toLocaleDateString() : 'Sin fecha'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Disponible</Badge>
                      <Button size="sm" onClick={() => connectSheet(sheet.sheet_id)} disabled={connectingId === sheet.sheet_id} className="bg-blue-600 hover:bg-blue-700">
                        {connectingId === sheet.sheet_id ? (<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />) : (<><Plus className="h-4 w-4 mr-1" />Conectar</>)}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {filteredSheets.length === 0 && (!isAuthenticated || filteredAvailableSheets.length === 0) && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Sheet className="h-8 w-8 text-gray-400" /></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{!isAuthenticated ? 'Conecta Google Sheets' : 'No hay hojas disponibles'}</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">{!isAuthenticated ? 'Primero debes conectar al menos una hoja de Google Sheets para continuar con la creación de tu campaña.' : 'No se encontraron hojas de Google Sheets. Asegúrate de tener hojas de cálculo en tu Google Drive.'}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {/* ✅ BOTÓN ÚNICO: Verifica autenticación antes de actuar */}
              <Button onClick={handleConnectOrSelectSheets} disabled={authLoading} className="bg-blue-600 hover:bg-blue-700">
                {authLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Procesando...</>
                ) : isAuthenticated ? (
                  <><Sheet className="h-4 w-4 mr-2" />Seleccionar Google Sheets</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" />Conectar Google Sheets</>
                )}
              </Button>
              {isAuthenticated && (
                <Button variant="outline" onClick={() => loadAvailableSheets(1)} disabled={loading}><RotateCcw className="h-4 w-4 mr-2" />Actualizar Lista</Button>
              )}
              <GoogleSheetsPicker buttonText="Conectar hojas existentes" buttonVariant="outline" onSheetsSelected={async (pickedSheets) => {
                toast.success(`${pickedSheets.length} hoja(s) seleccionada(s)`);
                await loadAvailableSheets(1);
              }} multiSelect={true} />
            </div>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Anterior</Button>
          <Button 
            onClick={onNext} 
            disabled={!selectedSheetId || !sheetDataLoaded || loadingSheetData} 
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {loadingSheetData ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Cargando datos...
              </>
            ) : (
              <>
                Siguiente: Mapear Variables
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InlineSheetsSelector;
